import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Habit, HabitMonth, HabitsResponse } from '@td/shared';
import { get, post, patch, del, api } from '../lib/api';

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['habits'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useHabits(year: number, month: number, initialData?: HabitsResponse) {
  return useQuery({
    queryKey: ['habits', year, month],
    queryFn: () => get<HabitsResponse>(`/habits?year=${year}&month=${month}`),
    // The dashboard already fetched the current month; seeding it here avoids
    // an immediate second request for data we're holding.
    initialData,
    staleTime: 30_000,
  });
}

export function useCreateHabit() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { name: string; color?: string }) => post<Habit>('/habits', input),
    onSuccess: invalidate,
  });
}

export function useUpdateHabit() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; color?: string }) =>
      patch<Habit>(`/habits/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteHabit() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => del<void>(`/habits/${id}`),
    onSuccess: invalidate,
  });
}

export function useToggleHabitDay() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (v: { id: string; year: number; month: number; day: number; value: boolean }) =>
      api<HabitMonth>(`/habits/${v.id}/entry`, {
        method: 'PUT',
        body: JSON.stringify({ year: v.year, month: v.month, day: v.day, value: v.value }),
      }),

    // Filling a square should be instant; it's a one-tap action. Both the
    // dashboard payload and the standalone month query hold this grid, so
    // both are patched.
    onMutate: async (v) => {
      const monthKey = ['habits', v.year, v.month];
      await Promise.all([
        qc.cancelQueries({ queryKey: ['dashboard'] }),
        qc.cancelQueries({ queryKey: monthKey }),
      ]);

      /*
       * Every dashboard entry, not one.
       *
       * The dashboard is cached per day — ['dashboard', '2026-08-06'] — so an
       * exact-key read here would find nothing and the square would not fill
       * until the request came back.
       */
      const previousDash = qc.getQueriesData<unknown>({ queryKey: ['dashboard'] });
      const previousMonth = qc.getQueryData(monthKey);

      const patchGrid = (grid: HabitsResponse): HabitsResponse => ({
        ...grid,
        entries: grid.entries.map((e) =>
          e.habitId === v.id
            ? { ...e, days: e.days.map((d, i) => (i === v.day - 1 ? v.value : d)) }
            : e,
        ),
      });

      qc.setQueriesData<{ habits?: HabitsResponse } | undefined>(
        { queryKey: ['dashboard'] },
        (old) =>
          old?.habits && old.habits.year === v.year && old.habits.month === v.month
            ? { ...old, habits: patchGrid(old.habits) }
            : old,
      );
      qc.setQueryData(monthKey, (old: HabitsResponse | undefined) => (old ? patchGrid(old) : old));

      return { previousDash, previousMonth, monthKey };
    },
    onError: (_e, _v, ctx) => {
      for (const [key, data] of ctx?.previousDash ?? []) qc.setQueryData(key, data);
      if (ctx?.previousMonth) qc.setQueryData(ctx.monthKey, ctx.previousMonth);
    },
    onSettled: invalidate,
  });
}
